import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO27031_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO/IEC 27031 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            ICT Readiness for Business Continuity — Guidelines for ensuring ICT
            infrastructure supports business continuity during disruptions.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO/IEC 27031 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO/IEC 27031:2011 is the international standard for ICT Readiness
              for Business Continuity. It provides organizations with a
              framework to establish, implement, and maintain the ICT
              (Information and Communication Technology) infrastructure and
              processes necessary for ensuring business continuity during
              disruptions. The standard offers guidelines on how to build ICT
              resilience, ensuring that critical systems, services, and
              processes remain operational or can be quickly restored in the
              event of a disaster, cyberattack, or system failure.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO/IEC 27031 certification demonstrates a company’s
              commitment to business continuity and disaster recovery, ensuring
              that its ICT systems are fully prepared to support organizational
              operations during business disruptions. The certification is
              crucial for organizations that rely on ICT infrastructure to
              perform essential business functions and must ensure that this
              infrastructure can withstand and recover from potential risks and
              disruptions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO/IEC 27031 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO/IEC 27031 certification provides a structured approach to
              ensure that an organization’s ICT systems and information
              technology infrastructure are resilient, enabling the business to
              continue operating in the face of challenges and disruptions. This
              includes implementing business continuity management (BCM)
              practices specifically tailored to the organization’s ICT
              environment.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              The certification outlines processes for:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Identifying and assessing ICT risks that may impact business operations.",
                "Ensuring that critical ICT systems are protected, backed up, and can be restored in the event of a disruption.",
                "Establishing procedures for effective disaster recovery and maintaining business operations during incidents that impact ICT systems.",
                "Implementing preventive measures and ICT security controls to ensure the continued functioning of critical services.",
                "Testing and reviewing recovery procedures regularly to ensure readiness in times of disruption.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO/IEC 27031 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Business Continuity Framework for ICT",
                desc: "ISO/IEC 27031 provides a framework for ensuring that ICT systems are integrated into an organization’s overall business continuity management (BCM) strategy. This ensures that ICT resilience is prioritized as part of the continuity plan.",
              },
              {
                icon: Target,
                title: "Risk Assessment and Management",
                desc: "The standard emphasizes the need for ICT risk assessments to identify potential threats to ICT services and operations, including cyberattacks, natural disasters, hardware failures, and other disruptions that could impact critical systems.",
              },
              {
                icon: BarChart3,
                title: "Disaster Recovery and Incident Response",
                desc: "ISO/IEC 27031 includes guidelines for establishing effective disaster recovery plans and incident response procedures. This ensures that an organization can quickly recover from an ICT-related disruption, minimizing the impact on business operations.",
              },
              {
                icon: TrendingUp,
                title: "ICT Resilience Testing",
                desc: "The standard encourages regular testing and exercising of ICT continuity plans to verify their effectiveness in the event of a disruption. Testing ensures that organizations are prepared for unexpected incidents and can respond quickly and effectively.",
              },
              {
                icon: Globe,
                title: "Continuous Improvement",
                desc: "ISO/IEC 27031 promotes continuous improvement by regularly reviewing and updating ICT readiness plans. This ensures that business continuity procedures evolve as the organization’s technology infrastructure and risk landscape change over time.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO/IEC 27031 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Enhanced ICT Resilience — ISO/IEC 27031 ensures that your ICT infrastructure is resilient to disruptions, ensuring that critical systems remain operational or are restored quickly during emergencies or incidents.",
                "Reduced Downtime and Financial Impact — By improving ICT readiness, organizations can significantly reduce downtime and minimize the financial impact of disruptions. This helps maintain business operations, protects revenue streams, and preserves customer trust.",
                "Risk Mitigation — The standard helps organizations identify and mitigate ICT risks associated with their infrastructure, reducing the likelihood of service interruptions, data breaches, and cyberattacks.",
                "Compliance with Business Continuity Standards — Achieving ISO/IEC 27031 certification demonstrates compliance with recognized business continuity best practices, ensuring that your organization is prepared to meet legal, regulatory, and industry requirements for ICT resilience and disaster recovery.",
                "Stakeholder Confidence — ISO/IEC 27031 certification builds confidence among customers, partners, and investors by demonstrating your commitment to business continuity and ICT resilience. It shows that you have the systems and processes in place to ensure uninterrupted service during disruptions.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO/IEC 27031 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO/IEC 27031 focuses on ensuring that an organization’s ICT
              systems are ready to support business continuity in the face of
              disruptions. The key components include:
            </p>
            <div className="space-y-4">
              {[
                "1. ICT Risk Management and Assessment — Conduct risk assessments to identify potential threats to your ICT systems and business operations. This includes assessing risks related to cybersecurity, system failures, natural disasters, and human errors.",
                "2. Business Continuity Strategy for ICT — Develop and implement a comprehensive ICT business continuity strategy that aligns with your organization’s overall business continuity objectives. This strategy should address how to maintain or restore critical ICT services during and after a disruption.",
                "3. Disaster Recovery Planning — Establish clear disaster recovery plans that define how your organization will recover critical ICT systems and services in the event of a disruption. This includes data backup, system restoration, and communication protocols.",
                "4. Incident Response and Crisis Management — Implement incident response procedures for managing ICT-related crises. This includes defining roles and responsibilities, establishing communication channels, and ensuring that employees are trained to respond effectively to disruptions.",
                "5. Testing, Monitoring, and Review — Regularly test and exercise your ICT continuity plans to ensure that they are effective in the event of an emergency. Conduct mock drills and reviews to assess the adequacy of the recovery procedures and to identify areas for improvement.",
                "6. Continuous Improvement — Implement a continuous improvement process to regularly review and enhance your ICT resilience. This includes learning from past incidents, monitoring evolving risks, and adapting to changes in your organization’s ICT infrastructure and external environment.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO/IEC 27031 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Improved ICT Resilience",
                "Risk Mitigation and Disaster Recovery",
                "Regulatory Compliance",
                "Customer and Stakeholder Confidence",
                "Operational Efficiency",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO/IEC 27031 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The process for obtaining ISO/IEC 27031 certification typically
              includes the following steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Develop an ICT Business Continuity Strategy — Define the scope of your ICT continuity management system and develop a strategy to ensure that critical ICT services can continue or be quickly restored during a disruption.",
                "2. Conduct a Risk Assessment — Identify and assess the ICT risks that could impact business operations. This includes evaluating the potential impact of cyberattacks, system failures, data loss, and other disruptions.",
                "3. Create a Disaster Recovery Plan — Develop and implement a disaster recovery plan that outlines the steps for restoring critical ICT systems and data after a disruption. This plan should include procedures for data backup, system restoration, and communication.",
                "4. Implement Incident Response Procedures — Establish incident response protocols to ensure that your organization can respond effectively to ICT-related crises, such as data breaches or service disruptions.",
                "5. Testing and Review — Regularly test and exercise your ICT continuity plans to ensure that they work effectively. Conduct drills, review performance, and make improvements based on feedback and lessons learned.",
                "6. Third-Party Certification Audit — Engage an accredited certification body to conduct a third-party audit to verify compliance with ISO/IEC 27031 and assess the effectiveness of your ICT readiness plans.",
                "7. Continuous Monitoring and Improvement — Implement a process for monitoring and improving your ICT resilience over time, ensuring that your organization adapts to evolving risks, technologies, and business requirements.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO/IEC 27031 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides expert ISO/IEC 27031
              certification consultancy services, helping businesses ensure
              their ICT systems are resilient, minimize risks, and support
              business continuity in the face of disruptions.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "ICT Risk Assessment and Strategy Development",
                "Disaster Recovery and Incident Response Planning",
                "Testing and Drills for Business Continuity",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Continuous Monitoring and Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO/IEC 27031 certification, ensuring
              ICT readiness, business continuity, and disaster recovery
              capabilities.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO27031_Details;
